import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Input, Modal, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'hooks/apollo';

import Page from 'components/Page';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import StatusTag from 'components/StatusTag';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import { Link } from 'react-router-dom';
import { renderCustomer } from 'components/Table/renderers';
import messages from './messages';

import customersQuery from './customers.gql';
import createCustomerMutation from './createCustomerMutation.gql';

const CreateCustomerModal = ({ onDone }) => {
  const [visible, setVisible] = useState(false);
  const [mutate, { loading }] = useMutation(createCustomerMutation);
  const [form] = Form.useForm();

  const onCreateCustomer = async ({ name }) => {
    await mutate({ variables: { name } });
    onDone();
    setVisible(false);
  };

  return (
    <>
      <Button type="primary" onClick={() => setVisible(true)} icon={<PlusOutlined />}>
        <FormattedMessage {...messages.create} />
      </Button>
      <Modal
        title={<FormattedMessage {...messages.create} />}
        open={visible}
        onClose={() => setVisible(false)}
        onCancel={() => setVisible(false)}
        onOk={form.submit}
        confirmLoading={loading}
      >
        <Form form={form} onFinish={onCreateCustomer} layout="vertical">
          <Form.Item
            name="name"
            label={<FormattedMessage {...globalMessages.name} />}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const lengthColumn = (dataIndex, message = globalMessages[dataIndex]) => ({
  dataIndex,
  title: <FormattedMessage {...message} />,
  sorter: (a, b) => a[dataIndex].length - b[dataIndex].length,
  render: (value) => value?.length,
  align: 'right',
});

const CustomerList = () => {
  const $t = useFormatMessage();
  const [search, setSearch] = useState();
  const { data = {}, loading, refetch } = useQuery(customersQuery);
  const { changeCustomer } = useUser();

  const onSwitchCustomer = async (customer) => {
    await changeCustomer(customer.id);
  };

  const actions = (record) => [
    <Link key="edit" to={`/administration/customers/${record.id}`}>
      <Button type="primary">
        Edit
      </Button>
    </Link>,
    <Button key="switch" onClick={() => onSwitchCustomer(record)}>
      Switch to
    </Button>,
  ];

  const columns = [
    {
      dataIndex: 'logo',
      title: <FormattedMessage {...messages.logo} />,
      render: (logo) => logo && <img src={logo.url} style={{ maxHeight: 40, maxWidth: 100 }} alt="logo" />,
      align: 'center',
      className: 'thumbnails',
    },
    {
      dataIndex: 'name',
      title: <FormattedMessage {...globalMessages.name} />,
      render: (_, row) => renderCustomer(row),
      defaultSortOrder: 'ascend',
    },
    {
      dataIndex: 'createdAt',
      title: <FormattedMessage {...globalMessages.created} />,
      type: 'datetime',
    },
    lengthColumn('websites', globalMessages.dataSources),
    lengthColumn('customerAccess', globalMessages.users),
    {
      dataIndex: 'personalDataController',
      title: <FormattedMessage {...messages.personalDataController} />,
      type: 'bool',
      showSorterTooltip: {
        title: <FormattedMessage {...messages.personalDataControllerTooltip} />,
      },
    },
    {
      dataIndex: 'allowPersonalData',
      title: <FormattedMessage {...messages.allowPersonalData} />,
      type: 'bool',
      showSorterTooltip: {
        title: <FormattedMessage {...messages.allowPersonalDataTooltip} />,
      },
    },
    {
      dataIndex: 'status',
      title: <FormattedMessage {...globalMessages.status} />,
      render: (status) => <StatusTag status={status} />,
    },
  ];

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage {...globalMessages.customers} />}
        extra={<CreateCustomerModal onDone={refetch} />}
      />
      <Page.Content>
        <Input.Search
          onSearch={setSearch}
          allowClear
          placeholder={$t(globalMessages.search)}
          style={{ marginBottom: '1em' }}
        />
        <Table
          loading={loading}
          columns={columns}
          dataSource={data.customers}
          search={search}
          actions={actions}
        />

      </Page.Content>
    </Page>
  );
};

export default CustomerList;
